import React, { DragEventHandler, useCallback, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@ff-os/shared-react/components/buttons/button/Button";
import { selectAccessorySizes } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from "@ff-scene-plugins/photo-prints-redux/store";
import classNames from "classnames";
import { selectAccessoryId, setAccessoryId } from "../../../_redux-ui";
import { useMemoizedPhoto } from "../../../memoizations/_redux-memoization";
import { applyAccessoryToSinglePhoto } from "../../../thunks/applyAccessoryToSinglePhoto";
import { PhotoPrintFrame } from "../../photo-print/PhotoPrintFrame";
import { PhotoPrintDesktopOverlay } from "./PhotoPrintDesktopOverlay";
import { PhotoPrintQuantity } from "./PhotoPrintQuantity";
import styles from "./PhotoPrintDesktop.module.scss";

type PhotoPrintDesktopProps = {
  id: string;
  className?: string;
  setDragDisabledOnGroup: (value: boolean) => void;
};

const PhotoPrintDesktopComponent = ({ id, className, setDragDisabledOnGroup }: PhotoPrintDesktopProps) => {

    const { t } = useTranslation();
    const dispatch = useDispatch<PhotoPrintsPdpDispatch>();
    const photo = useMemoizedPhoto(id);
    const accessoryId = useSelector((state: PhotoPrintsPdpState) => selectAccessoryId(state));
    const accessories = useSelector((state: PhotoPrintsPdpState) => selectAccessorySizes(state));

    const photoRef = useRef<HTMLImageElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [hasAccessoryDraggedOver, setHasAccessoryDraggedOver] = useState(false);
    const dragLeaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Check if we should show click-to-apply overlay for this photo
    const selectedAccessory = accessoryId ? accessories.find(acc => acc.id === accessoryId) : null;
    const isSingleAccessory = selectedAccessory && (!selectedAccessory.minimumQuantity || selectedAccessory.minimumQuantity === 1);
    const hasActiveClickToApply = !!(accessoryId && isSingleAccessory);

    const onDrag: DragEventHandler<HTMLDivElement> = useCallback(event => {

        event.dataTransfer.setData('photoId', id);
        event.dataTransfer.setData('groupId', photo.groupId);
        event.dataTransfer.setDragImage(
            photoRef.current!,
            0.5 * photoRef.current!.clientWidth,
            0.5 * photoRef.current!.clientHeight);

        setDragDisabledOnGroup(true);
        setIsDragging(true);
    }, [id, photo.groupId, setDragDisabledOnGroup]);

    const onDragEnd = () => {
        setDragDisabledOnGroup(false);
        setIsDragging(false);
    };

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        const isSinglePhotoAccessory = e.dataTransfer.types.includes('singlephotoaccessory');

        if (isSinglePhotoAccessory) {
            e.preventDefault();
            e.stopPropagation();

            if (!hasAccessoryDraggedOver) {
                setHasAccessoryDraggedOver(true);
            }

            if (dragLeaveTimeoutRef.current) {
                clearTimeout(dragLeaveTimeoutRef.current);
                dragLeaveTimeoutRef.current = null;
            }
        }
    }, [hasAccessoryDraggedOver]);

    const handleDragLeave = useCallback(() => {

        if (dragLeaveTimeoutRef.current) {
            clearTimeout(dragLeaveTimeoutRef.current);
        }

        dragLeaveTimeoutRef.current = setTimeout(() => {
            setHasAccessoryDraggedOver(false);
        }, 50);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        const isSinglePhotoAccessory = e.dataTransfer.types.includes('singlephotoaccessory');

        if (isSinglePhotoAccessory) {
            e.preventDefault();
            e.stopPropagation();

            const accessoryId = e.dataTransfer.getData('accessoryId');
            if (accessoryId) {
                dispatch(applyAccessoryToSinglePhoto({
                    photoId: id,
                    accessoryId: accessoryId
                }));
            }
        }


        if (dragLeaveTimeoutRef.current) {
            clearTimeout(dragLeaveTimeoutRef.current);
            dragLeaveTimeoutRef.current = null;
        }
        setHasAccessoryDraggedOver(false);
    }, [dispatch, id]);

    const handleClickToApply = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (accessoryId && isSingleAccessory) {
            dispatch(applyAccessoryToSinglePhoto({
                photoId: id,
                accessoryId: accessoryId
            }));
            dispatch(setAccessoryId(""));  // Clear selection after applying
        }
    }, [dispatch, id, accessoryId, isSingleAccessory]);

    useEffect(() => {
        return () => {
            if (dragLeaveTimeoutRef.current) {
                clearTimeout(dragLeaveTimeoutRef.current);
            }
        };
    }, []);

    const overlayClasses = classNames(styles.overlay, {
        [styles.overlayDisabled]: isDragging
    });

    return (
        <div className={classNames(styles.container, className)}>
            <PhotoPrintFrame photoId={id}
                ref={photoRef}
                disabled={photo.status === 'error'}
                onDragStart={onDrag}
                onDragEnd={onDragEnd}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                hasAccessoryDraggedOver={hasAccessoryDraggedOver}>

                {photo.status !== 'error' && (
                    <PhotoPrintDesktopOverlay photoId={id} className={overlayClasses} />
                )}

                {hasActiveClickToApply && photo.status !== 'error' && (
                    <div className={styles.clickToApplyOverlay} onClick={handleClickToApply}>
                        <Button className={styles.clickToApplyButton} style="smallest">
                            {t('photo_prints.click_to_apply_inline')}
                        </Button>
                    </div>
                )}
            </PhotoPrintFrame>

            {photo.status !== 'error' && (
                <PhotoPrintQuantity className={styles.input} photoId={id} />
            )}
        </div>
    );
};

export const PhotoPrintDesktop = React.memo(PhotoPrintDesktopComponent);